import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'

interface UIState {
  // Layout state
  sidebarOpen: boolean
  sidebarCollapsed: boolean

  // Theme and appearance
  theme: 'light' | 'dark' | 'system'

  // Modal and dialog state
  modals: {
    createFAQ: boolean
    editFAQ: boolean
    deleteFAQ: boolean
    bulkActions: boolean
    settings: boolean
    help: boolean
  }

  // Panel states
  panels: {
    searchFilters: boolean
    inspector: boolean
    preview: boolean
  }

  // Editor state
  editor: {
    mode: 'edit' | 'preview' | 'split'
    fullscreen: boolean
    showLineNumbers: boolean
    wordWrap: boolean
  }

  // Toast notifications
  toasts: Array<{
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    title: string
    message: string
    duration?: number
    action?: {
      label: string
      onClick: () => void
    }
  }>

  // Loading states for different features
  loadingStates: {
    search: boolean
    save: boolean
    upload: boolean
    export: boolean
    sync: boolean
  }

  // View preferences
  view: {
    layout: 'list' | 'grid' | 'table'
    density: 'compact' | 'comfortable' | 'spacious'
    sortBy: 'updated_at' | 'created_at' | 'question' | 'usage_count'
    sortOrder: 'asc' | 'desc'
  }

  // Feature preferences
  preferences: {
    enableBulkOperations: boolean
    enableTurboMode: boolean
  }

  // Recent activity
  recentSearches: string[]
  recentFAQs: string[]

  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setTheme: (theme: 'light' | 'dark' | 'system') => void

  // Modal management
  openModal: (modal: keyof UIState['modals']) => void
  closeModal: (modal: keyof UIState['modals']) => void
  closeAllModals: () => void

  // Panel management
  togglePanel: (panel: keyof UIState['panels']) => void
  setPanel: (panel: keyof UIState['panels'], open: boolean) => void

  // Editor management
  setEditorMode: (mode: UIState['editor']['mode']) => void
  toggleEditorFullscreen: () => void
  setEditorPreference: (key: keyof Omit<UIState['editor'], 'mode' | 'fullscreen'>, value: boolean) => void

  // Toast management
  addToast: (toast: Omit<UIState['toasts'][0], 'id'>) => void
  removeToast: (id: string) => void
  clearToasts: () => void

  // Loading state management
  setLoadingState: (feature: keyof UIState['loadingStates'], loading: boolean) => void

  // View management
  setViewLayout: (layout: UIState['view']['layout']) => void
  setViewDensity: (density: UIState['view']['density']) => void
  setSort: (sortBy: UIState['view']['sortBy'], sortOrder: UIState['view']['sortOrder']) => void

  // Preferences management
  setPreference: (key: keyof UIState['preferences'], value: boolean) => void
  syncPreferencesFromSupabase: () => Promise<void>
  syncPreferencesToSupabase: () => Promise<void>

  // Recent activity
  addRecentSearch: (query: string) => void
  addRecentFAQ: (id: string) => void
  clearRecentSearches: () => void
  clearRecentFAQs: () => void
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        sidebarOpen: true,
        sidebarCollapsed: false,
        theme: 'system',

        modals: {
          createFAQ: false,
          editFAQ: false,
          deleteFAQ: false,
          bulkActions: false,
          settings: false,
          help: false
        },

        panels: {
          searchFilters: false,
          inspector: false,
          preview: false
        },

        editor: {
          mode: 'split',
          fullscreen: false,
          showLineNumbers: true,
          wordWrap: true
        },

        toasts: [],

        loadingStates: {
          search: false,
          save: false,
          upload: false,
          export: false,
          sync: false
        },

        view: {
          layout: 'list',
          density: 'comfortable',
          sortBy: 'updated_at',
          sortOrder: 'desc'
        },

        preferences: {
          enableBulkOperations: false,
          enableTurboMode: false
        },

        recentSearches: [],
        recentFAQs: [],

        // Actions
        toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),

        setSidebarCollapsed: (collapsed: boolean) => set({ sidebarCollapsed: collapsed }),

        setTheme: (theme: 'light' | 'dark' | 'system') => {
          set({ theme })

          // Apply theme to document
          const root = document.documentElement
          if (theme === 'dark') {
            root.classList.add('dark')
          } else if (theme === 'light') {
            root.classList.remove('dark')
          } else {
            // System theme
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
            if (prefersDark) {
              root.classList.add('dark')
            } else {
              root.classList.remove('dark')
            }
          }
        },

        // Modal management
        openModal: (modal: keyof UIState['modals']) => {
          set(state => ({
            modals: { ...state.modals, [modal]: true }
          }))
        },

        closeModal: (modal: keyof UIState['modals']) => {
          set(state => ({
            modals: { ...state.modals, [modal]: false }
          }))
        },

        closeAllModals: () => {
          set(state => ({
            modals: Object.keys(state.modals).reduce((acc, key) => {
              acc[key as keyof UIState['modals']] = false
              return acc
            }, {} as UIState['modals'])
          }))
        },

        // Panel management
        togglePanel: (panel: keyof UIState['panels']) => {
          set(state => ({
            panels: { ...state.panels, [panel]: !state.panels[panel] }
          }))
        },

        setPanel: (panel: keyof UIState['panels'], open: boolean) => {
          set(state => ({
            panels: { ...state.panels, [panel]: open }
          }))
        },

        // Editor management
        setEditorMode: (mode: UIState['editor']['mode']) => {
          set(state => ({
            editor: { ...state.editor, mode }
          }))
        },

        toggleEditorFullscreen: () => {
          set(state => ({
            editor: { ...state.editor, fullscreen: !state.editor.fullscreen }
          }))
        },

        setEditorPreference: (key: keyof Omit<UIState['editor'], 'mode' | 'fullscreen'>, value: boolean) => {
          set(state => ({
            editor: { ...state.editor, [key]: value }
          }))
        },

        // Toast management
        addToast: (toast: Omit<UIState['toasts'][0], 'id'>) => {
          const id = Math.random().toString(36).substr(2, 9)
          const newToast = { ...toast, id }

          set(state => ({
            toasts: [...state.toasts, newToast]
          }))

          // Auto-remove toast after duration
          if (toast.duration !== 0) {
            setTimeout(() => {
              get().removeToast(id)
            }, toast.duration || 5000)
          }
        },

        removeToast: (id: string) => {
          set(state => ({
            toasts: state.toasts.filter(toast => toast.id !== id)
          }))
        },

        clearToasts: () => set({ toasts: [] }),

        // Loading state management
        setLoadingState: (feature: keyof UIState['loadingStates'], loading: boolean) => {
          set(state => ({
            loadingStates: { ...state.loadingStates, [feature]: loading }
          }))
        },

        // View management
        setViewLayout: (layout: UIState['view']['layout']) => {
          set(state => ({
            view: { ...state.view, layout }
          }))
        },

        setViewDensity: (density: UIState['view']['density']) => {
          set(state => ({
            view: { ...state.view, density }
          }))
        },

        setSort: (sortBy: UIState['view']['sortBy'], sortOrder: UIState['view']['sortOrder']) => {
          set(state => ({
            view: { ...state.view, sortBy, sortOrder }
          }))
        },

        // Recent activity
        addRecentSearch: (query: string) => {
          set(state => ({
            recentSearches: [
              query,
              ...state.recentSearches.filter(search => search !== query)
            ].slice(0, 10) // Keep only 10 recent searches
          }))
        },

        addRecentFAQ: (id: string) => {
          set(state => ({
            recentFAQs: [
              id,
              ...state.recentFAQs.filter(faqId => faqId !== id)
            ].slice(0, 10) // Keep only 10 recent FAQs
          }))
        },

        clearRecentSearches: () => set({ recentSearches: [] }),
        clearRecentFAQs: () => set({ recentFAQs: [] }),

        // Preferences management
        setPreference: (key: keyof UIState['preferences'], value: boolean) => {
          set(state => ({
            preferences: { ...state.preferences, [key]: value }
          }))
          // Auto-sync to Supabase when preference changes
          get().syncPreferencesToSupabase()
        },

        syncPreferencesFromSupabase: async () => {
          try {
            const { data: { user }, error } = await supabase.auth.getUser()
            if (error || !user) return

            const preferences = user.user_metadata?.preferences
            if (preferences) {
              set(state => ({
                preferences: { ...state.preferences, ...preferences }
              }))
            }
          } catch (error) {
            console.error('Failed to sync preferences from Supabase:', error)
          }
        },

        syncPreferencesToSupabase: async () => {
          try {
            const { data: { user }, error: userError } = await supabase.auth.getUser()
            if (userError || !user) return

            const { preferences } = get()
            const { error } = await supabase.auth.updateUser({
              data: {
                preferences
              }
            })

            if (error) {
              console.error('Failed to sync preferences to Supabase:', error)
            }
          } catch (error) {
            console.error('Failed to sync preferences to Supabase:', error)
          }
        }
      }),
      {
        name: 'ui-store',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          theme: state.theme,
          editor: state.editor,
          view: state.view,
          preferences: state.preferences,
          recentSearches: state.recentSearches,
          recentFAQs: state.recentFAQs
        })
      }
    ),
    {
      name: 'ui-store'
    }
  )
)